import { ChevronDown } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  type AppointmentBookingStatus,
  BOOKING_STATUS_VALUES,
  getBookingStatusInfo,
} from "@/domains/appointment/appointments/shared/utils/appointment-status-style";

interface AppointmentStatusDropdownProps {
  appointmentId: string;
  currentStatus: string;
  onStatusChange?: (status: string) => void;
}

export function AppointmentStatusDropdown({
  appointmentId: _appointmentId,
  currentStatus,
  onStatusChange,
}: AppointmentStatusDropdownProps) {
  const { t } = useTranslation("newCalendar");
  const [isUpdating, setIsUpdating] = useState(false);

  const handleStatusChange = async (newStatus: string) => {
    if (newStatus === currentStatus) return;

    setIsUpdating(true);
    try {
      // Call the parent component's status change handler
      // This will trigger the GraphQL mutation in the confirmation view
      await onStatusChange?.(newStatus);
    } catch (error) {
      console.error("Failed to update appointment status:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  const statusInfo = getBookingStatusInfo(currentStatus as AppointmentBookingStatus);
  const StatusIconComponent = statusInfo.icon;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={`h-6 px-2 gap-1 border ${statusInfo.backgroundColor} ${statusInfo.borderColor} hover:opacity-80`}
          onClick={(e) => e.stopPropagation()}
        >
          {isUpdating ? (
            <>
              <div className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
              <span className="text-xs font-medium">{t("common.updating")}</span>
            </>
          ) : (
            <>
              <StatusIconComponent className={`w-3 h-3 ${statusInfo.iconColor}`} />
              <span className={`text-xs font-medium ${statusInfo.textColor}`}>
                {statusInfo.text}
              </span>
              <ChevronDown className="w-3 h-3" />
            </>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {BOOKING_STATUS_VALUES.map((status) => {
          const optionStatusInfo = getBookingStatusInfo(status);
          const OptionIconComponent = optionStatusInfo.icon;
          return (
            <DropdownMenuItem
              key={status}
              onClick={(e) => {
                e.stopPropagation();
                handleStatusChange(status);
              }}
              disabled={currentStatus === status}
            >
              <OptionIconComponent className={`w-3 h-3 ${optionStatusInfo.iconColor} mr-2`} />
              {optionStatusInfo.text}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
